import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { concatMap, distinctUntilChanged } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { DirectoryResultsStoreService } from './../../services/directory-results-store/directory-results-store.service';
import { DirectorySearchService } from './../../services/directory-search/directory-search.service';
import { DirectorySharedDataService } from 'src/app/directory/services/directory-shared-data.service';
import { DirectoryUtilsService } from '../../services/directory-utils/directory-utils.service';

import { OpenedElement } from './../../models/directory-opened-element.model';
import { DirectoryConfig, DirectoryFieldForFilter } from 'src/app/directory/models/directory-config.model';
import { DirectoryEntitySummary } from 'src/app/directory/models/directory-entity-summary.model';
import { DirectoryEntityDialogComponent } from '../dialog/directory-entity-dialog/directory-entity-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { DirectoryMapComponent } from '../directory-map/directory-map.component';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';

@Component({
  selector: 'app-directory',
  templateUrl: './directory.component.html',
  styleUrls: ['./directory.component.scss'],
})
export class DirectoryComponent implements OnInit, OnDestroy {
  public widgetConfig: DirectoryConfig;
  public hasListDisplayed: boolean;
  public hasMapDisplayed: boolean;
  public hasLegendDisplayed: boolean;
  public tmpWidgetConfig: DirectoryConfig;
  public availableFields: any[];
  public hasLegend: boolean = false;

  public currentOpenedEntityId: DirectoryEntitySummary['entity_id'];
  public openedLinkedElementId: string;
  public goBack: boolean = false;

  public isEntitySheetOpened: boolean = false;
  public isLinkedElementSheetOpened: boolean = false;
  public subscriptionLevels: Subscription;
  public subscriptionFields: Subscription;

  public tmpOpenedElement: OpenedElement;

  public criteriaFilters: string = null;

  private screenTooSmallForMapAndList: boolean;

  @Output() closeDialog = new EventEmitter<boolean>();

  @ViewChild(DirectoryMapComponent) directoryMap: DirectoryMapComponent;

  constructor(
    private route: ActivatedRoute,
    public directoryUtils: DirectoryUtilsService,
    private resultsStore: DirectoryResultsStoreService,
    private directoryData: DirectorySharedDataService,
    private directorySearch: DirectorySearchService,
    private translate: TranslateService,
    private router: Router,
    private dialog: MatDialog,
    public breakpointObserver: BreakpointObserver,
  ) {
    if (window.innerWidth <= 699) {
      // console.debug("Screen is starting as small");
      this.directoryData.changeMapAndListDisplayability(false);
    } else {
      // console.debug("Screen is starting as NOT small");
      this.directoryData.changeMapAndListDisplayability(true);
    }

    // detect screen size changes
    this.breakpointObserver.observe([
      "(max-width: 699px)", "(max-width: 599px)", "(max-width: 499px)", "(max-width: 399px)"
    ]).subscribe((result: BreakpointState) => {
      if (result.matches) {
        // console.debug("Screen is now considered small");
        this.directoryData.changeMapAndListDisplayability(false);
      } else {
        // console.debug("Screen is now NOT considered small");
        this.directoryData.changeMapAndListDisplayability(true);
      }
    });

  }

  ngOnInit(): void {
    this.directoryData.widgetId = this.route.snapshot.paramMap.get('widgetId');

    this.translate.use(this.route.snapshot.paramMap.get('lang'));
    this.directoryData.widgetLang = this.route.snapshot.paramMap.get('lang');
    this.directorySearch.widgetLang = this.route.snapshot.paramMap.get('lang');

    this.subscriptionFields = this.directoryData.findDirectoryConfig(this.route.snapshot.paramMap.get('widgetId')).pipe(
      concatMap((config: DirectoryConfig) => {
        // console.log("Full directory config", config);
        // Check if there are any filters criteria from the URL
        if (this.route.snapshot.queryParamMap.has('criteria')) {
          // If the header bar is not being displayed, filter results from start
          if (!config.layout.hasFilters) {
            this.directorySearch.setParamCriteriaString = this.route.snapshot.queryParamMap.get('criteria');
          } else {
            // We need to pass the criteria's values to initialise our filters
            this.criteriaFilters = this.route.snapshot.queryParamMap.get('criteria');
            this.directorySearch.setParamCriteriaString = this.route.snapshot.queryParamMap.get('criteria');
          }
        }

        this.directoryUtils.isHostAuthorized(config.authorizedHosts)

        if(config.addRequests && config.addRequests.forceAddRequests && !this.route.snapshot.url.map(x => x.path).includes("demande-ajout")) {
          this.router.navigateByUrl(
            `/${this.route.snapshot.paramMap.get('lang')}/annuaire/${this.route.snapshot.paramMap.get('widgetId')}/demande-ajout`
            );
        }

        this.setHeaderFieldsLabelsforEdit(config);

        this.directoryData.setHeaders(config.apiKey);
        this.directorySearch.setHeaders(config.apiKey);
        this.directorySearch.setConcernedSubject(config.isFocusOnAssociatedDB, config.concernedAssociatedDB);
        this.directoryUtils.setSheet(config.addedStyleLink);
        // console.log('initDirectoryComp')
        if(!this.directoryData.isInitialized()) this.directoryData.setAvailableFields();
        this.tmpWidgetConfig = config;

        this.hasLegend = this.tmpWidgetConfig.layout.hasLegend

        return this.directoryData.isScreenLargeEnoughForMapAndList();
      }),
      concatMap((state) =>{
        this.screenTooSmallForMapAndList = !state;

        const listState:boolean = this.isElementDisplayed(
          this.tmpWidgetConfig.layout?.core?.hasList,
          this.tmpWidgetConfig.layout?.init?.hasListDisplayedOnInit
        );

        const mapState:boolean = this.isElementDisplayed(
          this.tmpWidgetConfig.layout?.core?.hasMap,
          this.tmpWidgetConfig.layout?.init?.hasMapDisplayedOnInit
        );

        // #383 here
        if (this.screenTooSmallForMapAndList && listState && mapState) {
          this.directoryData.changeListState(true);
          this.directoryData.changeMapState(false);
        } else {
          this.directoryData.changeListState(listState);
          this.directoryData.changeMapState(mapState);
        }

        return this.directoryData.getAvailableFields();
      })).subscribe((fields) => {
        // console.log("FieldsDirectoryComp",fields)
        if (fields && !this.directoryData.isInitialized()) {
          // console.log("Reset fields and config FieldsDirectoryComp")
          this.availableFields = fields;
          this.addPropertiesToFields();
          this.widgetConfig = this.tmpWidgetConfig;
          // console.log("widgetConfigDirectoryComp-Change",this.widgetConfig)
          this.directoryData.changeWidgetConfig(this.widgetConfig);
          this.directorySearch.findResults();
        } else if (fields && this.directoryData.isInitialized()) {
          this.availableFields = fields;
          this.addPropertiesToFields();
          this.widgetConfig = this.tmpWidgetConfig;
          if (this.tmpOpenedElement) {
            this.resultsStore.setOpenedElement(this.tmpOpenedElement);
          }
        }

      });

    this.directoryData.getHasListDisplayed()
      .subscribe((state) => {
        this.hasListDisplayed = state;
      });
    this.directoryData.getHasMapDisplayed()
      .subscribe((state) => {
        this.hasMapDisplayed = state;
      });
    this.directoryData.getHasLegendDisplayed()
      .subscribe((state) => {
      this.hasLegendDisplayed = state;
    });

    this.directoryData.isScreenLargeEnoughForMapAndList().pipe(distinctUntilChanged()).subscribe((state) =>{
      this.screenTooSmallForMapAndList = !state;  
      // #383 here
      if (this.screenTooSmallForMapAndList && this.hasListDisplayed && this.hasMapDisplayed) {
        this.directoryData.changeListState(true);
        this.directoryData.changeMapState(false);
      }
    });

    // Work with OpenedElement details components only if fullSize layout has not been activated,
    // otherwise we'll open a MatDialog
    if ((!this.subscriptionLevels || this.subscriptionLevels.closed) && !(this.widgetConfig?.layout.isFullSize)) {
      this.subscriptionLevels = this.resultsStore.getOpenedElement().subscribe((openedElement: OpenedElement) => {
        if (openedElement) { //Executes once the element is selected
          if (!this.widgetConfig) {
            this.tmpOpenedElement = openedElement;
          } else {
            if (openedElement && openedElement.id) {
              if (openedElement.isMain) this.resultsStore.setParentOpenedElement(openedElement);

              if (openedElement.isMain || this.goBack) { //Opens main Element
                this.currentOpenedEntityId = openedElement.id;
                this.openedLinkedElementId = null;
                this.closeMainElement();
                this.openMainElement(openedElement.id);
                this.closeLinkedElement();
                this.goBack = false;
              } else { //Opens linked Element
                this.currentOpenedEntityId = null;
                this.openedLinkedElementId = openedElement.id;
                this.openLinkedElement(openedElement.id);
                this.closeMainElement();
              }
            }
          }
        } else { //Starts with everything closed + close button clicked
          this.currentOpenedEntityId = null;
          this.openedLinkedElementId = null;
          this.goBack = false;
          this.closeMainElement();
          this.closeLinkedElement();
        }
      });
    }
  }

  /**
   * Complete the fields stored in the directory configuration with info obtained from "dynamic" fields (ie: info live queried)
   */
  public addPropertiesToFields(){

    // Completing fields from the main sheet (widget main focus)
    this.tmpWidgetConfig.sheet.fieldsgroup.forEach((group) =>
      group.fields.forEach((field) => {
        const dynamicField = this.availableFields.find((x) =>
          x.field_id === field.id
        );
        field.domain = dynamicField?.associated_data_domain_id;
        field.range = dynamicField?.associated_data_id;
        field.readOnly = dynamicField?.readonly;
        field.isExternal = dynamicField?.isExternal;
        field.interestArea = dynamicField?.interest_area?.interest_area_id;
        if (dynamicField != undefined) {
          const fieldCommunities = [];
          dynamicField.communities.forEach(community => {
            fieldCommunities.push(community.community_id);
          });
          field.communities = fieldCommunities;
        }
    }));

    // Completing fields from secondary sheets
    this.tmpWidgetConfig?.secondarySheets?.forEach(sheet => {
      sheet?.fieldsgroup?.forEach((group) =>
          group.fields.forEach((field) => {
            const dynamicField = this.availableFields.find((x) =>
              x.field_id === field.id
            );
            field.domain = dynamicField?.associated_data_domain_id;
            field.range = dynamicField?.associated_data_id;
            field.readOnly = dynamicField?.readonly;
            field.isExternal = dynamicField?.isExternal;
            field.interestArea = dynamicField?.interest_area?.interest_area_id;
            if (dynamicField != undefined) {
              const fieldCommunities = [];
              dynamicField.communities.forEach(community => {
                fieldCommunities.push(community.community_id);
              });
              field.communities = fieldCommunities;
            }
        }));
    });
    
    // Completing fields for add requests forms for widget main elements
    this.tmpWidgetConfig?.addRequests?.fieldsgroup.forEach((group) =>
      group.fields.forEach((field) => {
        const dynamicField = this.availableFields.find((x) =>
          x.field_id === field.id
        );
        // field.associated_data_id = dynamicField?.associated_data_domain_id; // WTF ouf never used in creation forms
        field.range = dynamicField?.associated_data_id; // Is used
        field.readOnly = dynamicField?.readonly;
        field.isExternal = dynamicField?.isExternal;
        field.communityRestriction = dynamicField?.accessible_communities;
        field.interestAreaRestriction = dynamicField?.accessible_interest_areas;
        field.ancestry = dynamicField?.ancestry;
        field.interestArea = dynamicField?.interest_area?.interest_area_id;
        if (dynamicField != undefined) {
          const fieldCommunities = [];
          dynamicField.communities.forEach(community => {
            fieldCommunities.push(community.community_id);
          });
          field.communities = fieldCommunities;
        }
    }));

    // Completing fields for add requests forms for widget secondary elements
    this.tmpWidgetConfig?.addSubRequests?.subResourceList?.forEach(subResource => {
      subResource?.fieldsgroup?.forEach((group) =>
          group.fields.forEach((field) => {
            const dynamicField = this.availableFields.find((x) =>
              x.field_id === field.id
            );
            field.range = dynamicField?.associated_data_id; // Is used
            field.readOnly = dynamicField?.readonly;
            field.isExternal = dynamicField?.isExternal;
            field.communityRestriction = dynamicField?.accessible_communities;
            field.interestAreaRestriction = dynamicField?.accessible_interest_areas;
            field.ancestry = dynamicField?.ancestry;
            field.interestArea = dynamicField?.interest_area?.interest_area_id;
            if (dynamicField != undefined) {
              const fieldCommunities = [];
              dynamicField.communities.forEach(community => {
                fieldCommunities.push(community.community_id);
              });
              field.communities = fieldCommunities;
            }
        }));
    }); 

    // Completing fields for filters
    this.tmpWidgetConfig?.filters?.list.forEach((filter: DirectoryFieldForFilter) => {
      if (filter.type == 'LinkedChoice') {
        // update filter properties to be more dynamic
        const dynamicField = this.availableFields.find((x) =>
          x.field_id === filter.id
        );
        filter.range = dynamicField?.associated_data_id
        filter.associated_data_id = dynamicField?.associated_data_id
        filter.ancestry = dynamicField?.ancestry
    }});    
  }

  public setHeaderFieldsLabelsforEdit(config) {
    if(!config.concernedAssociatedDB || config.concernedAssociatedDB == "") {
      config.concernedAssociatedDB = null;
    }

    if(config?.sheet?.description?.id) {
      config.sheet.description.domain = config.concernedAssociatedDB;
      config.sheet.description['overrideTitle'] = {'fr':"Description",'en':"Description"};
      config.sheet.description['showfieldTitle'] = true;
    }
    if(config?.sheet?.banner?.id) {
      config.sheet.banner.domain = config.concernedAssociatedDB;
      config.sheet.banner['overrideTitle'] = {'fr':"Bannière",'en':"Banner"};
      config.sheet.banner['showfieldTitle'] = true;
    }
    if(config?.sheet?.logo?.id) {
      config.sheet.logo.domain = config.concernedAssociatedDB;
      config.sheet.logo['overrideTitle'] = {'fr':"Logo",'en':"Logo"};
      config.sheet.logo['showfieldTitle'] = true;
    }

    if(config?.sheet?.title?.id) {
      config.sheet.title.domain = config.concernedAssociatedDB;
    }

    if (config?.secondarySheets) {
      config.secondarySheets.forEach( sheet => {
        if (sheet.classId != 'entityForm') {
          sheet.title.domain = sheet.classId;
          sheet.logo.domain = sheet.classId;
          sheet.description.domain = sheet.classId;
          sheet.banner.domain = sheet.classId;
        } // else ? put null
        if(sheet?.description?.id) {
          sheet.description['overrideTitle'] = {'fr':"Description",'en':"Description"};
          sheet.description['showfieldTitle'] = true;
        }
        if(sheet?.banner?.id) {
          sheet.banner['overrideTitle'] = {'fr':"Bannière",'en':"Banner"};
          sheet.banner['showfieldTitle'] = true;
        }
        if(sheet?.logo?.id) {
          sheet.logo['overrideTitle'] = {'fr':"Logo",'en':"Logo"};
          sheet.logo['showfieldTitle'] = true;
        }
      })
    }
  }

  ngOnDestroy(): void {
    this.subscriptionFields.unsubscribe();
    this.subscriptionLevels.unsubscribe();
  }

  public openEntityInDialog() {
    if (this.resultsStore.getOpenedElementSync()) { // si l'élément n'est pas un cluster
      if (this.widgetConfig.layout.isFullSize) {
        this.dialog.open(DirectoryEntityDialogComponent, {
          width: '1280px',
          maxWidth: '100vw',
          maxHeight: '90vh',
          panelClass: 'entity-sheet-dialog',
          closeOnNavigation: false
        });
      };
    } 
  };

  ///// DIFFERENT LAYOUTS /////
  // NOTE: This feels like it could be a single variable using an enum instead of 3 functions

  // #383 here
  public isLayoutMixed(): boolean {
    if (this.hasMapDisplayed && this.widgetConfig.layout.core.hasList) {
      return true;
    }
    return false;
  }

  public isLayoutListOnly(): boolean {
    if ((!this.hasMapDisplayed && this.widgetConfig.layout.core.hasList)) {
      return true;
    }
    return false;
  }

  public isLayoutMapOnly(): boolean {
    if (this.widgetConfig.layout.core.hasMap && !this.widgetConfig.layout.core.hasList) {
      return true;
    }
    return false;
  }

  private isElementDisplayed(hasElement: boolean, hasElementDisplayedOnInit: boolean): boolean {
    if (hasElement && hasElementDisplayedOnInit) {
      return true;
    }
    return false;
  }

  ///// ELEMENTS POSITIONING /////

  public isListOnTheRight(): string {
    if (this.widgetConfig && this.widgetConfig?.layout.isListOnLeft) {
      return 'start';
    }
    return 'end';
  }

  public isLegendOnTheRight(): string {
    if (this.widgetConfig && this.widgetConfig?.layout.isLegendOnLeft) {
      return 'start';
    }
    return 'end';
  }

  public isSheetOnTheRight(): string {
    if (this.widgetConfig) {
      if (
        (!this.widgetConfig.layout.core.hasList && this.widgetConfig.layout.isLegendOnLeft)
        || (this.widgetConfig.layout.core.hasList && this.widgetConfig.layout.isListOnLeft)
      ) {
        return 'start';
      }
    }
    return 'end';
  }

  ///// /////

  public openMainElement(id: DirectoryEntitySummary['entity_id']): void {
    this.isEntitySheetOpened = true;
  }
  public closeMainElement(): void {
    this.isEntitySheetOpened = false;
  }

  public openLinkedElement(id: string): void {
    this.isLinkedElementSheetOpened = true;
  }
  public closeLinkedElement(): void {
    this.isLinkedElementSheetOpened = false;
  }

  stateOfSecondarySheet(state: boolean) {
    this.goBack = state;

    let parentOpenedElement: OpenedElement = this.resultsStore.getParentOpenedElement();
    this.resultsStore.setParentOpenedElement(null);
    this.resultsStore.setOpenedElement(parentOpenedElement);
  }

  receiveUpdateMapSignal(event:boolean) {
    this.directoryMap.resizeMap();
  }

}